import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import L from 'leaflet';
import 'leaflet-draw';
import {
  TAMANO_MARKER,
  iconoDeTipo,
  colorPorProfundidad,
  NOMBRES_TIPO,
} from '../utils/iconosEquipos';

const MapaPiscinas = forwardRef(function MapaPiscinas(
  {
    piscinas = [],
    onDibujar,
    onClickPiscina,
    piscinaAEditar,
    piscinaSeleccionada = null,
    modoEquipos = null,
    onAgregarEquipo,
    onEliminarEquipo,
    equiposTmp = null,
    modoBatimetria = false,
    profundidadActual = null,
    onAgregarPuntoBatimetria,
    onEliminarPuntoBatimetria,
    batimetriaTmp = null,
    mostrarAlimentadores = true,
    mostrarAireadores = true,
    mostrarEquipos = true,
    mostrarBatimetria = true,
    mostrarTecnologias = true,
    puntoTemporal = null,
  },
  ref
) {
  const contenedorRef = useRef(null);
  const mapRef = useRef(null);
  const capaPiscinasRef = useRef(null);
  const capaEquiposRef = useRef(null);
  const capaBatimetriaRef = useRef(null);
  const capaPuntoTemporalRef = useRef(null);
  const drawnItemsRef = useRef(null);

  const onDibujarRef = useRef(onDibujar);
  const onClickPiscinaRef = useRef(onClickPiscina);
  const piscinaAEditarRef = useRef(piscinaAEditar);
  const modoEquiposRef = useRef(modoEquipos);
  const onAgregarEquipoRef = useRef(onAgregarEquipo);
  const onEliminarEquipoRef = useRef(onEliminarEquipo);
  const piscinaSeleccionadaRef = useRef(piscinaSeleccionada);
  const modoBatimetriaRef = useRef(modoBatimetria);
  const profundidadActualRef = useRef(profundidadActual);
  const onAgregarPuntoBatimetriaRef = useRef(onAgregarPuntoBatimetria);
  const mostrarTecnologiasRef = useRef(mostrarTecnologias);

  useEffect(() => {
    onDibujarRef.current = onDibujar;
    onClickPiscinaRef.current = onClickPiscina;
    piscinaAEditarRef.current = piscinaAEditar;
    modoEquiposRef.current = modoEquipos;
    onAgregarEquipoRef.current = onAgregarEquipo;
    onEliminarEquipoRef.current = onEliminarEquipo;
    piscinaSeleccionadaRef.current = piscinaSeleccionada;
    modoBatimetriaRef.current = modoBatimetria;
    profundidadActualRef.current = profundidadActual;
    onAgregarPuntoBatimetriaRef.current = onAgregarPuntoBatimetria;
    mostrarTecnologiasRef.current = mostrarTecnologias;
  });

  const colores = ['#0D47A1', '#2E7D32', '#C62828', '#EF6C00', '#6A1B9A', '#00838F', '#AD1457'];
  const COLOR_COSECHADA = '#6B7280';

  useImperativeHandle(ref, () => ({
    activarDibujoPoligono: () => {
      if (!mapRef.current) return;
      if (drawnItemsRef.current) drawnItemsRef.current.clearLayers();
      const drawer = new L.Draw.Polygon(mapRef.current, {
        allowIntersection: false,
        shapeOptions: { color: '#0D47A1', weight: 3 },
      });
      drawer.enable();
    },
    cancelarDibujo: () => {
      if (drawnItemsRef.current) drawnItemsRef.current.clearLayers();
    },
    centrarEnPiscina: (piscina) => {
      if (!mapRef.current) return;
      if (piscina.geometria?.coordinates?.[0]) {
        const coords = piscina.geometria.coordinates[0].map(([lng, lat]) => [lat, lng]);
        const bounds = L.latLngBounds(coords);
        mapRef.current.fitBounds(bounds, { padding: [80, 80], maxZoom: 18 });
      }
    },
  }));

  // 1. Inicializar el mapa
  useEffect(() => {
    if (!contenedorRef.current) return;
    if (mapRef.current) return;

    if (contenedorRef.current._leaflet_id) {
      contenedorRef.current._leaflet_id = null;
    }

    const map = L.map(contenedorRef.current).setView([-3.2581, -79.9553], 15);
    mapRef.current = map;

    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Esri, Maxar',
      maxZoom: 22,
      maxNativeZoom: 19,
    }).addTo(map);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 22,
      opacity: 0.5,
    }).addTo(map);

    capaPiscinasRef.current = L.featureGroup().addTo(map);
    capaEquiposRef.current = L.featureGroup().addTo(map);
    capaBatimetriaRef.current = L.featureGroup().addTo(map);
    capaPuntoTemporalRef.current = L.featureGroup().addTo(map);
    drawnItemsRef.current = new L.FeatureGroup();
    map.addLayer(drawnItemsRef.current);

    map.on(L.Draw.Event.CREATED, (e) => {
      const layer = e.layer;
      drawnItemsRef.current.addLayer(layer);

      const latlngs = layer.getLatLngs()[0];
      const areaM2 = calcularArea(latlngs);
      const areaHa = parseFloat((areaM2 / 10000).toFixed(2));

      const geometria = {
        type: 'Polygon',
        coordinates: [latlngs.map((p) => [p.lng, p.lat])],
      };

      if (onDibujarRef.current) {
        onDibujarRef.current({ geometria, area_ha: areaHa, layer });
      }
    });

    let ultimoClickTs = 0;

    map.on('click', (e) => {
      const ahora = Date.now();
      if (ahora - ultimoClickTs < 300) return;
      ultimoClickTs = ahora;

      const { lat, lng } = e.latlng;

      if (modoBatimetriaRef.current) {
        const prof = profundidadActualRef.current;
        if (prof != null && !isNaN(prof) && onAgregarPuntoBatimetriaRef.current) {
          onAgregarPuntoBatimetriaRef.current({ lat, lng, profundidad: Number(prof) });
        }
        return;
      }

      const modo = modoEquiposRef.current;
      if (!modo) return;
      const piscina = piscinaSeleccionadaRef.current;
      if (!piscina) return;

      if (onAgregarEquipoRef.current) {
        onAgregarEquipoRef.current({ lat, lng, tipo: modo });
      }
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    const container = mapRef.current.getContainer();
    container.style.cursor = (modoEquipos || modoBatimetria) ? 'crosshair' : '';
  }, [modoEquipos, modoBatimetria]);

  // ✅ Piscinas (con geolocalización inteligente como fallback)
  useEffect(() => {
    if (!capaPiscinasRef.current) return;
    if (!Array.isArray(piscinas)) return;

    capaPiscinasRef.current.clearLayers();

    const sectoresColor = {};
    let colorIdx = 0;

    piscinas.forEach((p) => {
      if (!p.geometria || !p.geometria.coordinates) return;

      const esLaQueEditamos = piscinaAEditar && piscinaAEditar.id === p.id;
      const esLaSeleccionada = piscinaSeleccionada && piscinaSeleccionada.id === p.id;
      const esCosechada = !p.activo;

      if (!sectoresColor[p.sector_id]) {
        sectoresColor[p.sector_id] = colores[colorIdx % colores.length];
        colorIdx++;
      }

      let color;
      if (esLaQueEditamos) color = '#F59E0B';
      else if (esLaSeleccionada) color = '#FFC107';
      else if (esCosechada) color = COLOR_COSECHADA;
      else color = sectoresColor[p.sector_id];

      const coords = p.geometria.coordinates[0].map(([lng, lat]) => [lat, lng]);
      const poly = L.polygon(coords, {
        color,
        weight: esLaQueEditamos ? 5 : esLaSeleccionada ? 4 : esCosechada ? 2 : 3,
        fillOpacity: esCosechada ? 0.25 : 0.35,
        fillColor: color,
        dashArray: esLaQueEditamos ? '10, 10' : esCosechada ? '5, 5' : null,
      });

      if (mostrarTecnologiasRef.current) {
        const etiquetaHTML = `
          <div style="text-align:center;">
            <div style="font-weight:bold;">${p.codigo}</div>
            <div style="font-size:9px; color:${esCosechada ? '#6B7280' : '#fff'}; opacity:0.9;">
              ${p.sector_nombre || ''}
            </div>
            ${p.tecnologia ? `<div style="font-size:8px; color:${esCosechada ? '#9CA3AF' : '#E0F2FE'}; opacity:0.8;">${p.tecnologia}</div>` : ''}
          </div>
        `;

        poly.bindTooltip(etiquetaHTML, {
          permanent: true,
          direction: 'center',
          className: 'etiqueta-piscina',
          opacity: 1,
        });
      }

      poly.on('click', (e) => {
        if (modoBatimetriaRef.current) {
          const prof = profundidadActualRef.current;
          if (prof != null && !isNaN(prof) && onAgregarPuntoBatimetriaRef.current) {
            onAgregarPuntoBatimetriaRef.current({
              lat: e.latlng.lat,
              lng: e.latlng.lng,
              profundidad: Number(prof),
            });
          }
          return;
        }

        if (modoEquiposRef.current) {
          if (onAgregarEquipoRef.current) {
            onAgregarEquipoRef.current({
              lat: e.latlng.lat,
              lng: e.latlng.lng,
              tipo: modoEquiposRef.current,
            });
          }
        } else {
          if (onClickPiscinaRef.current) onClickPiscinaRef.current(p);
        }
      });

      capaPiscinasRef.current.addLayer(poly);
    });

    // ✅ LÓGICA DE CENTRADO INTELIGENTE
    if (piscinaAEditar && mapRef.current) {
      // 1. Si estamos editando una piscina, centrar en esa piscina
      if (piscinaAEditar.geometria?.coordinates?.[0]) {
        const coords = piscinaAEditar.geometria.coordinates[0].map(([lng, lat]) => [lat, lng]);
        const bounds = L.latLngBounds(coords);
        mapRef.current.fitBounds(bounds, { padding: [80, 80], maxZoom: 18 });
      }
    } else if (!piscinaSeleccionada && piscinas.length > 0 && mapRef.current) {
      // 2. Si NO hay piscina seleccionada pero hay piscinas, encuadrar TODAS las piscinas
      const bounds = capaPiscinasRef.current.getBounds();
      if (bounds.isValid()) {
        mapRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 18 });
      }
    } else if (piscinas.length === 0 && mapRef.current) {
      // 3. SOLO si NO hay piscinas dibujadas, usar geolocalización como fallback
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            mapRef.current.setView([latitude, longitude], 18);
            L.circleMarker([latitude, longitude], {
              radius: 8,
              fillColor: '#3B82F6',
              color: '#ffffff',
              weight: 2,
              opacity: 1,
              fillOpacity: 0.9,
            })
              .addTo(mapRef.current)
              .bindTooltip('📍 Estás aquí', { direction: 'top' });
          },
          (error) => {
            console.warn('No se pudo obtener la geolocalización:', error.message);
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      }
    }
  }, [piscinas, piscinaAEditar, piscinaSeleccionada, mostrarTecnologias]);

  // ✅ Equipos
  useEffect(() => {
    if (!capaEquiposRef.current) return;
    capaEquiposRef.current.clearLayers();

    if (!piscinaSeleccionada) return;

    const crearMarker = (eq, tipo, idx) => {
      const src = iconoDeTipo(tipo);
      const icon = L.divIcon({
        html: `
          <img 
            src="${src}" 
            alt="${tipo}" 
            style="width: ${TAMANO_MARKER}px; height: ${TAMANO_MARKER}px; 
                   filter: drop-shadow(0 2px 3px rgba(0,0,0,0.55));" 
          />
        `,
        className: 'equipo-marker-img',
        iconSize: [TAMANO_MARKER, TAMANO_MARKER],
        iconAnchor: [TAMANO_MARKER / 2, TAMANO_MARKER / 2],
      });

      const nombre = eq.nombre || `${NOMBRES_TIPO[tipo]} #${idx + 1}`;

      const marker = L.marker([eq.lat, eq.lng], { icon });
      marker.bindTooltip(nombre, { direction: 'top' });

      marker.on('click', (ev) => {
        ev.originalEvent?.stopPropagation?.();
        if (onEliminarEquipoRef.current) {
          onEliminarEquipoRef.current({ index: idx, tipo });
        }
      });
      return marker;
    };

    const safeArr = (v) => {
      if (Array.isArray(v)) return v;
      if (typeof v === 'string') {
        try {
          const p = JSON.parse(v);
          return Array.isArray(p) ? p : [];
        } catch { return []; }
      }
      return [];
    };

    const alims = equiposTmp && Array.isArray(equiposTmp.alimentadores)
      ? equiposTmp.alimentadores
      : safeArr(piscinaSeleccionada.geometria_alimentadores);
    const airs = equiposTmp && Array.isArray(equiposTmp.aireadores)
      ? equiposTmp.aireadores
      : safeArr(piscinaSeleccionada.geometria_aireadores);
    const eqs = equiposTmp && Array.isArray(equiposTmp.equipos)
      ? equiposTmp.equipos
      : safeArr(piscinaSeleccionada.geometria_equipos);

    if (mostrarAlimentadores) {
      alims.forEach((eq, i) => capaEquiposRef.current.addLayer(crearMarker(eq, 'alimentador', i)));
    }
    if (mostrarAireadores) {
      airs.forEach((eq, i) => capaEquiposRef.current.addLayer(crearMarker(eq, 'aireador', i)));
    }
    if (mostrarEquipos) {
      eqs.forEach((eq, i) => capaEquiposRef.current.addLayer(crearMarker(eq, 'equipo', i)));
    }
  }, [equiposTmp, piscinaSeleccionada, mostrarAlimentadores, mostrarAireadores, mostrarEquipos]);

  // ✅ Batimetría
  useEffect(() => {
    if (!capaBatimetriaRef.current) return;
    capaBatimetriaRef.current.clearLayers();

    if (!piscinaSeleccionada) return;
    if (!mostrarBatimetria) return;

    const puntos = batimetriaTmp
      || (Array.isArray(piscinaSeleccionada.batimetria) ? piscinaSeleccionada.batimetria : []);

    if (puntos.length === 0) return;

    const profundidades = puntos
      .map((p) => Number(p.profundidad))
      .filter((n) => !isNaN(n));
    const min = Math.min(...profundidades);
    const max = Math.max(...profundidades);

    puntos.forEach((p, i) => {
      const prof = Number(p.profundidad);
      const color = colorPorProfundidad(prof, min, max);

      const circle = L.circleMarker([p.lat, p.lng], {
        radius: 10,
        color: '#ffffff',
        weight: 2,
        fillColor: color,
        fillOpacity: 0.85,
      });

      circle.bindTooltip(`🌊 ${prof.toFixed(2)} m`, { direction: 'top' });

      circle.on('click', (ev) => {
        ev.originalEvent?.stopPropagation?.();
        if (onEliminarPuntoBatimetria) {
          onEliminarPuntoBatimetria(i);
        }
      });

      capaBatimetriaRef.current.addLayer(circle);
    });
  }, [batimetriaTmp, piscinaSeleccionada, mostrarBatimetria, onEliminarPuntoBatimetria]);

  // ✅ Marcador temporal
  useEffect(() => {
    if (!capaPuntoTemporalRef.current || !mapRef.current) return;
    capaPuntoTemporalRef.current.clearLayers();

    if (!puntoTemporal) return;

    const { lat, lng, nombre } = puntoTemporal;

    const icon = L.divIcon({
      html: `
        <div style="
          width: 30px; height: 30px;
          background: #F59E0B;
          border: 3px solid #fff;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-weight: bold;
          font-size: 12px;
        ">📍</div>
      `,
      className: 'punto-temporal',
      iconSize: [30, 30],
      iconAnchor: [15, 15],
    });

    const marker = L.marker([lat, lng], { icon }).addTo(capaPuntoTemporalRef.current);

    if (nombre) {
      marker.bindTooltip(nombre, { direction: 'top', permanent: false });
    }

    mapRef.current.setView([lat, lng], 18);
  }, [puntoTemporal]);

  return <div ref={contenedorRef} style={{ width: '100%', height: '100%', minHeight: '500px' }} />;
});

export default MapaPiscinas;

function calcularArea(latlngs) {
  const R = 6378137;
  let area = 0;
  const n = latlngs.length;
  for (let i = 0; i < n; i++) {
    const p1 = latlngs[i];
    const p2 = latlngs[(i + 1) % n];
    area +=
      ((p2.lng - p1.lng) * Math.PI / 180) *
      (2 + Math.sin((p1.lat * Math.PI) / 180) + Math.sin((p2.lat * Math.PI) / 180));
  }
  area = (area * R * R) / 2;
  return Math.abs(area);
}